import type { LtdCandidate } from './composite-geometry';

/**
 * The Add Wellbore tearsheet's LTD catalog.
 *
 * `POST /queries/ltd_candidates` is UNSCOPED — unlike `/queries/inventory_unit_slots`, which
 * always reads inside a Type Curve version or an Inventory Workspace. It is narrowed only by
 * the rail facets, a text search, the map extent and paging.
 */

/**
 * The live rail facets, in the order they appear in the rail (the UAT mock's order). This is a
 * DISPLAY order only — the backend computes each facet's options against every OTHER active filter,
 * so narrowing is order-independent and this order does not set any precedence.
 */
export const LTD_FACETS = [
  'typeCurveScenario',
  'basin',
  'typeCurveArea',
  'formation',
  'inventory',
  'unitSurveyArea',
] as const;

export type LtdFacet = (typeof LTD_FACETS)[number];

export const LTD_FACET_LABELS: Record<LtdFacet, string> = {
  typeCurveScenario: 'Type Curve Scenario',
  basin: 'Basin',
  typeCurveArea: 'Type Curve Area',
  formation: 'Formation',
  inventory: 'Inventory',
  unitSurveyArea: 'Unit / Survey Area',
};

const SERVER_KEY: Record<LtdFacet, string> = {
  typeCurveScenario: 'type_curve_scenario',
  basin: 'basin',
  typeCurveArea: 'type_curve_area',
  formation: 'formation',
  inventory: 'inventory',
  unitSurveyArea: 'unit_survey_area',
};

/** The map extent, as the endpoint takes it: [minLon, minLat, maxLon, maxLat]. */
export type LtdBbox = [number, number, number, number];

export interface LtdCandidatesQuery {
  /** Free text over Property ID and name. Server-side — never filter a page in the client. */
  text?: string;
  /** One or more selected values per facet; empty means the facet is not narrowing. */
  facets?: Partial<Record<LtdFacet, string[]>>;
  bbox?: LtdBbox;
  page: number;
  pageSize: number;
}

export interface LtdCandidatesResponse {
  results: LtdCandidate[];
  total: number;
  /**
   * Per facet, the values still reachable given the OTHER active filters — which is what
   * makes the rail progressive, and what lets a facet be widened again after narrowing.
   */
  facets: Partial<Record<LtdFacet, string[]>>;
}

/**
 * Build the request body.
 *
 * Empty facets and a blank search are OMITTED rather than sent empty: the backend reads an
 * absent filter as "no constraint", while an empty array would be a filter matching nothing.
 * @param {LtdCandidatesQuery} query The current rail state.
 * @return {Record<string, unknown>} The `query` envelope.
 */
export const ltdCandidatesBody = (
  query: LtdCandidatesQuery
): Record<string, unknown> => {
  const body: Record<string, unknown> = {
    page: query.page,
    page_size: query.pageSize,
  };

  if (query.text?.trim()) body.q = query.text.trim();
  if (query.bbox) body.bbox = query.bbox;

  LTD_FACETS.forEach((facet) => {
    const values = query.facets?.[facet] ?? [];
    if (values.length > 0) body[SERVER_KEY[facet]] = values;
  });

  return body;
};
